import React, { useEffect, useState } from 'react';
import { MemoryRouter, Route, Routes, UNSAFE_LocationContext as LocationContext, UNSAFE_RouteContext as RouteContext } from 'react-router-dom';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import Colors from '../../library/Colors';
import RMAScreen from '../../screens/rma/RmaScreen';
import RMADetailScreen from '../../screens/rma/RmaDetailScreen';
import { SFT_RMA_KEY, sftRmaUrl } from '../../library/sftRma';

const HEADER_H = 56;

/**
 * The RMA module of SoftTouch Vlaanderen in a full-screen dialog (RMA button on logging/add).
 *
 * The RMA screens are rendered directly with the SoftTouch Vlaanderen key (props.api), not in an iframe:
 * an iframe would load a second MySoftTouch that shares this tab's sessionStorage and would switch the
 * support tab's selected database. Overview <-> detail navigation runs in its own MemoryRouter, so the
 * page behind the dialog keeps its URL. The location and route contexts are reset first: react-router does not
 * allow a router inside a router, and without the route reset the inner <Routes> would match below logging/add.
 */
const SftRmaDialog = ({ open, onClose, lang, t, theme, login, notify, screenWidth }) => {
    const [height, setHeight] = useState(window.innerHeight);
    useEffect(() => {
        const onResize = () => setHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const dark = theme === 'dark';
    const common = {
        login, t, lang, theme, notify, screenWidth,
        api: SFT_RMA_KEY,
        db: {},
        hideSidebar: true,
        changeScreen: () => {}, // the app shell behind the dialog must not switch screens
    };

    return (
        <Dialog fullScreen open={open} onClose={onClose}
            PaperProps={{ className: theme, sx: { backgroundColor: dark ? Colors.dark_basic : Colors.light_basic, display: 'flex', flexDirection: 'column' } }}>
            <Box sx={{ height: HEADER_H, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5, px: 2, backgroundColor: dark ? Colors.dark_accent : Colors.sft_blue, color: '#fff' }}>
                <BrokenImageIcon />
                <Typography sx={{ fontSize: 17, fontWeight: 700, mr: 'auto' }}>RMA · SoftTouch Vlaanderen</Typography>
                <Tooltip title={t('open_new_tab')}>
                    <IconButton component="a" href={sftRmaUrl(lang)} target="_blank" rel="noopener noreferrer" aria-label={t('open_new_tab')} sx={{ color: '#fff' }}>
                        <OpenInNewIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('close')}>
                    <IconButton onClick={onClose} aria-label={t('close')} sx={{ color: '#fff' }}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            {/* .expanded reserves 240px for the app sidebar; there is no sidebar in the dialog. */}
            <Box sx={{ flex: '1 1 auto', overflow: 'auto', px: { xs: 1, md: 3 }, pb: 3,
                '& .expanded': { marginLeft: '0 !important', marginRight: '0 !important', width: '100% !important' } }}>
                {open && (
                    <LocationContext.Provider value={null}>
                      <RouteContext.Provider value={{ outlet: null, matches: [], isDataRoute: false }}>
                        <MemoryRouter initialEntries={['/' + lang + '/rma']}>
                            <Routes>
                                <Route path="/:lang/rma" element={<RMAScreen {...common} />} />
                                <Route path="/:lang/rma/:search" element={<RMAScreen {...common} />} />
                                <Route path="/:lang/rma/detail/:rmaId" element={<RMADetailScreen {...common} maxHeight={height - HEADER_H} />} />
                                <Route path="/:lang/rma/detail/:rmaId/:storeId" element={<RMADetailScreen {...common} maxHeight={height - HEADER_H} />} />
                            </Routes>
                        </MemoryRouter>
                      </RouteContext.Provider>
                    </LocationContext.Provider>
                )}
            </Box>
        </Dialog>
    );
};

export default SftRmaDialog;
